import { useGlobalToasts } from '@src/hooks/Toast/useGlobalToasts';
import { EMPTY_OBJECT } from '@src/utils/empty';
import { logger } from '@src/utils/logger';
import { useEffect } from 'react';
import { Dialog } from '../components/Dialog';
import { JsonModal } from '../components/JsonModal/JsonModal';
import { resolveJsonModalDocuments } from '../components/JsonModal/jsonModalUtils';
import type { JsonModalResolvedDocument } from '../components/JsonModal/types';
import { NotificationVariants } from '../components/Notification';
import { useMixpanel } from '../contexts/MixpanelContext';
import { MixpanelEvent } from '../tokens/mixpanel';
import { useCallbackRef } from './useCallbackRef';
import { useDisclosureWithContext } from './useDisclosureWithContext';

interface UseJsonModalParams {
  /** When handleClickJson is invoked, you can have a customMixpanelEvent be sent. The default sent Mixpanel event is `MixpanelEvent.ShowJSON`. */
  customMixpanelEvent?: MixpanelEvent;
}

/**
 * A helper hook encapsulating the logic needed to show a Json Modal with some data inside it
 * @returns `handleClickJson`, to be invoked whenever you want to show some data in the json modal, and then `jsonModal` which is the ReactNode
 * of the json modal itself
 */
export function useJsonModal<R>({ customMixpanelEvent }: UseJsonModalParams = EMPTY_OBJECT) {
  const jsonModal = useDisclosureWithContext<JsonModalResolvedDocument[]>();
  const mixpanel = useMixpanel();
  const { add } = useGlobalToasts();

  const handleClickJson = useCallbackRef((data: R | undefined) => {
    mixpanel.track(customMixpanelEvent ?? MixpanelEvent.ShowJSON);
    try {
      jsonModal.open(resolveJsonModalDocuments(data));
    } catch (caught) {
      const error = caught instanceof Error ? caught : new Error(String(caught));
      logger.error(error);
      add({
        text: `Unable to display JSON: ${error.message}`,
        variant: NotificationVariants.Negative,
      });
    }
  });

  const closeJsonModal = useCallbackRef(() => {
    jsonModal.close();
  });

  useEffect(() => {
    if (!jsonModal.isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      closeJsonModal();
      event.stopPropagation();
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [closeJsonModal, jsonModal.isOpen]);

  return {
    handleClickJson,
    jsonModal: (
      <Dialog
        key="json-modal"
        isOpen={jsonModal.isOpen}
        close={jsonModal.close}
        onCloseComplete={jsonModal.onCloseComplete}
        dataTestId="show-json-modal"
        title="Show JSON"
        showClose
        suppressFooter
      >
        {jsonModal.context ? (
          <JsonModal key={jsonModal.openId} documents={jsonModal.context} isVisible={jsonModal.isOpen} />
        ) : null}
      </Dialog>
    ),
  };
}
